import { PageFlip } from 'page-flip';
import { assetUrl } from './asset-url.js';
import { bindKeyboardNavigation, shouldUseReaderMode } from './accessibility.js';
import { renderBookPages, renderReader } from './templates.js';
import { createScene } from './scene/index.js';
import { createClosedBook } from './scene/closed-book.js';
import { getTier, isLandscape, isShortLandscape, watchTier } from './scene/tier.js';

const LAST_PAGE_KEY_PREFIX = 'interactive-book:last-page:';
// Matches the real book: the spread scans are 5340x3200, so one page is
// 0.834 w/h, and the page textures cropped from the reference agree (0.830).
const PAGE_WIDTH = 560;
const PAGE_HEIGHT = 675;
const SINGLE_ASPECT = PAGE_WIDTH / PAGE_HEIGHT;
const SPREAD_ASPECT = (PAGE_WIDTH * 2) / PAGE_HEIGHT;

/**
 * StPageFlip drops to a single page when the block is narrower than
 * `minWidth * 2`, so that threshold is how the tier picks its layout: a phone,
 * and a tablet held upright, read one page at a time.
 */
function minWidthForTier(tier) {
  // Also the engine's hard floor for the block width, so it has to stay below
  // what the stage can actually give it on a small screen.
  if (tier === 'phone') return 240;
  if (tier === 'tablet' && !isLandscape()) return 420;
  // A phone held sideways has the width for a spread but only ~400px of
  // height, which caps the stage at roughly 530px: the default floor of 280
  // (i.e. 560 for two pages) silently dropped it back to one page.
  if (isShortLandscape()) return 220;
  return 280;
}

/**
 * The engine clamps its own height, so on a short landscape screen the default
 * floor would make the book taller than the frame that has to hold it.
 */
function minHeightForTier() {
  return isShortLandscape() ? 250 : 380;
}

function expectedSinglePage(tier) {
  return tier === 'phone' || (tier === 'tablet' && !isLandscape());
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function createBookController({ data, elements, audio, labels }) {
  /**
   * The cover is deliberately withheld from StPageFlip. With `showCover` the
   * engine forces page 0 to hard density (PageCollection.ts:61), and hard pages
   * carry `backface-visibility: hidden` with no reverse element, so the cover
   * blinks out of existence halfway through its turn. Holding it back also lets
   * the closed state be a real object with a page block, which the engine has
   * no way to express — see scene/closed-book.js.
   */
  const hasCover = data.pages[0]?.type === 'cover';
  const interior = hasCover ? data.pages.slice(1) : data.pages;
  const interiorData = { ...data, pages: interior };
  const firstInteriorIndex = hasCover ? 1 : 0;

  let pageFlip = null;
  let scene = null;
  let closedBook = null;
  let tier = getTier();
  let mode = shouldUseReaderMode() ? 'reader' : hasCover ? 'closed' : 'open';
  let modeChosenByUser = false;
  let currentPage = Number(window.localStorage.getItem(`${LAST_PAGE_KEY_PREFIX}${data.meta.lang}`) || 0);
  let resizeTimer = null;
  // The engine's size floors are construction-time settings, so the mount has
  // to remember which ones it was built with. Rotating an iPad keeps the tier
  // at `tablet` and therefore never fired onTierChange, leaving a portrait
  // floor of 420 in place after the device turned sideways.
  let mountedFloors = '';
  // The camera fly-in belongs to the very first appearance of the book.
  // Opening the cover (and any later remount) rebuilds the scene, which must
  // read as the same object continuing, not as a page reload.
  let introPlayed = false;

  currentPage = clamp(currentPage, 0, Math.max(0, data.pages.length - 1));
  // The session always starts at the closed cover: the intro lands on the
  // object as photographed (main cover reference), never mid-book.

  function toInterior(pageIndex) {
    return clamp(pageIndex - firstInteriorIndex, 0, Math.max(0, interior.length - 1));
  }

  function updateProgress(pageIndex = currentPage) {
    currentPage = clamp(pageIndex, 0, Math.max(0, data.pages.length - 1));
    window.localStorage.setItem(`${LAST_PAGE_KEY_PREFIX}${data.meta.lang}`, String(currentPage));
    elements.progress.textContent = `${labels.progress} ${currentPage + 1} / ${data.pages.length}`;
    elements.previous.disabled = currentPage <= 0;
    elements.next.disabled = currentPage >= data.pages.length - 1;
  }

  /**
   * The page images are lazy `<img>`s, so the network only sees a spread when
   * StPageFlip brings it near the viewport — on a throttled connection every
   * turn landed on a page still loading. After each turn the next few spreads
   * are fetched ahead, one at a time (nearest first, then one page back), so
   * the warm HTTP cache answers the actual flip. Sequential on purpose: a
   * parallel burst would compete with the page being read right now.
   */
  const PRELOAD_AHEAD = 5;
  const preloaded = new Set();
  let preloadToken = 0;

  function pageImageUrls(page) {
    const urls = [];
    if (typeof page?.background?.src === 'string') urls.push(assetUrl(page.background.src));
    for (const item of page?.elements ?? []) {
      if (item?.type !== 'hotspot') continue;
      if (typeof item.image === 'string') urls.push(assetUrl(item.image));
      if (typeof item.patch === 'string') urls.push(assetUrl(item.patch));
    }
    return urls;
  }

  function preloadAround(pageIndex) {
    const token = ++preloadToken;
    const queue = [];
    for (let step = 1; step <= PRELOAD_AHEAD; step++) queue.push(pageIndex + step);
    queue.push(pageIndex - 1);
    const urls = queue
      .flatMap((i) => pageImageUrls(data.pages[i]))
      .filter((u) => !preloaded.has(u));
    void (async () => {
      for (const url of urls) {
        if (token !== preloadToken) return;
        await new Promise((done) => {
          const img = new Image();
          img.decoding = 'async';
          if ('fetchPriority' in img) img.fetchPriority = 'low';
          img.onload = () => {
            preloaded.add(url);
            done();
          };
          img.onerror = done;
          img.src = url;
        });
      }
    })();
  }

  /**
   * Without `showCover` a lone page only appears at the very end, and only when
   * the interior has an odd number of leaves. It sits in the left slot.
   */
  function loneFace(interiorIndex) {
    if (!pageFlip || pageFlip.getOrientation() !== 'landscape') return 'single';
    const isLast = interiorIndex === interior.length - 1;
    return isLast && interior.length % 2 === 1 ? 'left' : 'spread';
  }

  function syncFraming(pageIndex = currentPage) {
    if (!scene) return;

    if (mode === 'closed') {
      scene.setFrameOffset(0);
      scene.scene.dataset.bookFace = 'single';
      return;
    }

    const interiorIndex = toInterior(pageIndex);
    const face = loneFace(interiorIndex);
    const pageWidth = pageFlip ? pageFlip.getBoundsRect().pageWidth : 0;
    scene.setFrameOffset(face === 'left' ? pageWidth / 2 : 0);
    scene.scene.dataset.bookFace = face;
    scene.setBlock({
      left: interiorIndex + (hasCover ? 1 : 0),
      right: Math.max(0, interior.length - interiorIndex)
    });
  }

  function syncAspect() {
    if (!scene) return;

    let single;
    if (mode === 'closed') single = true;
    else if (pageFlip) single = pageFlip.getOrientation() === 'portrait';
    else single = expectedSinglePage(tier);

    scene.scene.style.setProperty('--spread-aspect', (single ? SINGLE_ASPECT : SPREAD_ASPECT).toFixed(4));
  }

  function teardownStage() {
    if (pageFlip) {
      pageFlip.destroy();
      pageFlip = null;
    }
    if (closedBook) {
      closedBook.destroy();
      closedBook = null;
    }
    if (scene) {
      scene.destroy();
      scene = null;
    }
  }

  function buildScene(state) {
    tier = getTier();
    elements.root.dataset.mode = 'book';
    elements.root.dataset.tier = tier;
    // Short on the face, full sentence for assistive technology: the control
    // sits in the footer beside the page counter, where "Читать без эффектов"
    // would outweigh everything else on the line.
    elements.modeToggle.textContent = labels.textMode;
    elements.modeToggle.setAttribute('aria-label', labels.reader);
    elements.modeToggle.setAttribute('aria-pressed', 'false');

    teardownStage();
    elements.viewport.replaceChildren();

    scene = createScene({
      viewport: elements.viewport,
      tier,
      standalone: elements.root.dataset.standalone === 'true',
      skipIntro: introPlayed,
      hint: labels.zoomHint
    });
    introPlayed = true;
    scene.scene.dataset.bookState = state;
  }

  function mountClosedMode() {
    mode = 'closed';
    buildScene('closed');
    syncAspect();

    // Only the cover is rendered here; the interior does not exist yet.
    const coverHost = document.createElement('div');
    renderBookPages(coverHost, { ...data, pages: [data.pages[0]] });

    closedBook = createClosedBook({
      host: scene.bookHost,
      coverPage: coverHost.firstElementChild,
      leafCount: interior.length,
      openLabel: labels.openBook,
      onOpen: () => mountOpenMode(firstInteriorIndex)
    });

    scene.attachStatic();
    scene.setTurning(false);
    scene.setBlock({ left: 0, right: interior.length });
    updateProgress(0);
    syncFraming(0);
    preloadAround(0);
  }

  function currentFloors() {
    return `${minWidthForTier(getTier())}x${minHeightForTier()}`;
  }

  function mountOpenMode(pageIndex = currentPage) {
    mode = 'open';
    buildScene('open');
    syncAspect();
    mountedFloors = currentFloors();

    renderBookPages(scene.bookHost, interiorData);

    pageFlip = new PageFlip(scene.bookHost, {
      width: PAGE_WIDTH,
      height: PAGE_HEIGHT,
      size: 'stretch',
      minWidth: minWidthForTier(tier),
      maxWidth: 620,
      minHeight: minHeightForTier(),
      maxHeight: 840,
      // The engine draws its fold shadows at startZIndex+10 — above the
      // flipping leaf (+4/+5) — so any visible amount makes the moving page
      // look transparent near the binding. Shadowing is the scene's job
      // (stage__turn-shadow); the engine draws none.
      maxShadowOpacity: 0,
      // The cover is ours, so the engine must not reserve a lone slot for it —
      // and with showCover off no page is forced to hard density.
      showCover: false,
      mobileScrollSupport: true,
      // Input is owned by scene/pointer-bridge.js so page dragging stays in
      // sync with the camera transform. See that file for why.
      useMouseEvents: false,
      usePortrait: true,
      startPage: toInterior(pageIndex),
      flippingTime: 780,
      autoSize: true
    });

    pageFlip.on('flip', (event) => {
      const dataIndex = Number(event.data) + firstInteriorIndex;
      updateProgress(dataIndex);
      syncFraming(dataIndex);
      preloadAround(dataIndex);
      void audio.playFlip();
    });

    // The engine reports user_fold / flipping / read; anything that is not
    // `read` means a leaf is off the block and the shadow should respond.
    pageFlip.on('changeState', (event) => {
      scene?.setTurning(event.data !== 'read');
    });

    pageFlip.on('changeOrientation', () => {
      syncAspect();
      pageFlip.update();
      syncFraming();
    });

    pageFlip.loadFromHTML(scene.bookHost.querySelectorAll('.book-page'));
    scene.attach(pageFlip, {
      // Pulling the first left page rightwards closes the book back onto the
      // cover — the same move the keyboard's "previous" already makes.
      onPrevAtStart: hasCover ? () => mountClosedMode() : undefined
    });

    // The stage guessed its ratio from the tier; now that the engine has laid
    // out for real, adopt its answer and let it re-measure the new box.
    syncAspect();
    pageFlip.update();

    scene.setTurning(false);
    updateProgress(Math.max(pageIndex, firstInteriorIndex));
    syncFraming();
    preloadAround(currentPage);
  }

  function mountReaderMode() {
    mode = 'reader';
    teardownStage();
    elements.root.dataset.mode = 'reader';
    elements.modeToggle.textContent = labels.book;
    elements.modeToggle.setAttribute('aria-label', labels.book);
    elements.modeToggle.setAttribute('aria-pressed', 'true');
    renderReader(elements.viewport, data);
    updateProgress(currentPage);

    const target = elements.viewport.querySelector(`[data-page-index="${currentPage}"]`);
    target?.scrollIntoView({ block: 'start' });
  }

  function goToReaderPage(nextIndex) {
    const pageIndex = clamp(nextIndex, 0, data.pages.length - 1);
    updateProgress(pageIndex);
    // Reader mode leaves out the pages that carry no text, so the wanted index
    // may not be on screen at all; scroll to the nearest one that is, in the
    // direction of travel, rather than doing nothing.
    const rendered = [...elements.viewport.querySelectorAll('[data-page-index]')];
    const forward = nextIndex >= currentPage;
    const target =
      elements.viewport.querySelector(`[data-page-index="${pageIndex}"]`) ??
      (forward
        ? rendered.find((el) => Number(el.dataset.pageIndex) >= pageIndex)
        : [...rendered].reverse().find((el) => Number(el.dataset.pageIndex) <= pageIndex));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function previous() {
    if (mode === 'reader') return goToReaderPage(currentPage - 1);
    if (mode === 'closed') return;
    // Turning back past the first interior leaf closes the book again.
    if (hasCover && toInterior(currentPage) === 0) return mountClosedMode();
    pageFlip?.flipPrev('bottom');
  }

  function next() {
    if (mode === 'reader') return goToReaderPage(currentPage + 1);
    if (mode === 'closed') return mountOpenMode(firstInteriorIndex);
    pageFlip?.flipNext('bottom');
  }

  /**
   * Reduced motion decides the *initial* mode, not what the reader is allowed
   * to choose. Without this, a reduced-motion visitor could never leave the
   * text version. Book mode stays honest under that preference: the camera
   * skips its intro and its zoom easing (see scene/camera.js).
   */
  function toggleMode() {
    modeChosenByUser = true;
    if (mode !== 'reader') return mountReaderMode();
    if (hasCover && currentPage === 0) mountClosedMode();
    else mountOpenMode(currentPage);
  }

  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      if (mode === 'reader') return;
      if (shouldUseReaderMode() && !modeChosenByUser) return mountReaderMode();
      // Rotation can change the engine's floors without changing the tier;
      // comparing the floors themselves keeps this to one remount per real
      // change instead of one per resize event.
      if (mode === 'open' && currentFloors() !== mountedFloors) return mountOpenMode(currentPage);
      syncAspect();
      syncFraming();
    }, 180);
  }

  function onTierChange(nextTier) {
    tier = nextTier;
    elements.root.dataset.tier = nextTier;
    if (mode === 'reader') return;

    if (mode === 'closed') {
      scene?.setTier(nextTier);
      syncAspect();
      return;
    }

    // minWidth is a construction-time setting, so a tier that changes the
    // single-page threshold needs a remount rather than a nudge.
    if (expectedSinglePage(nextTier) !== (pageFlip?.getOrientation() === 'portrait')) {
      mountOpenMode(currentPage);
      return;
    }

    scene?.setTier(nextTier);
    syncAspect();
    syncFraming();
  }

  elements.previous.addEventListener('click', previous);
  elements.next.addEventListener('click', next);
  elements.modeToggle.addEventListener('click', toggleMode);
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize, { passive: true });

  const unwatchTier = watchTier(onTierChange);

  const unbindKeyboard = bindKeyboardNavigation({
    onPrevious: previous,
    onNext: next,
    enabled: () => mode !== 'reader'
  });

  if (mode === 'reader') mountReaderMode();
  else if (mode === 'closed') mountClosedMode();
  else mountOpenMode(currentPage);

  return {
    previous,
    next,
    destroy() {
      window.clearTimeout(resizeTimer);
      teardownStage();
      unbindKeyboard();
      unwatchTier();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      elements.previous.removeEventListener('click', previous);
      elements.next.removeEventListener('click', next);
      elements.modeToggle.removeEventListener('click', toggleMode);
    }
  };
}
